// src/features/files/components/RenameFolderDialog.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import { foldersAPI } from '../services/folders';

export default function RenameFolderDialog({ open, folder, onClose, onSuccess }) {
  const { t } = useTranslation(['files', 'common']);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && folder) {
      setName(folder.folder_name || '');
      setError(null);
    }
  }, [open, folder]);

  const trimmed = name.trim();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!trimmed) {
      setError(t('files:errors.folder_name_required'));
      return;
    }
    setSaving(true);
    try {
      await foldersAPI.updateFolder(folder.folder_id, { folder_name: trimmed });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('common:errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('files:rename_folder')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <TextField
            label={t('files:folder_name')}
            value={name}
            onChange={(event) => { setName(event.target.value); setError(null); }}
            onFocus={(event) => event.target.select()}
            required
            fullWidth
            autoFocus
            disabled={saving}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>{t('common:buttons.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !trimmed || trimmed === folder?.folder_name}
          >
            {saving ? <CircularProgress size={24} /> : t('common:buttons.rename')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
